import { ACCENT2, AMBER, SIDEBAR_DIM } from "../theme";
import { DEMO_SIGNUP_HREF, DEMO_SIGNUP_LABEL } from "../demoMode";

// ─── SIDEBAR FOOTER ───────────────────────────────────────────────────────────
// Account area at the bottom of the desktop sidebar (220px wide, 14px padding, so 192px inside).
// Three stacked rows so nothing shares a line with anything else:
//   1. avatar and business name
//   2. QuickBooks status, then "Sync now" and "Set up" buttons (or "Sample numbers" in the demo)
//   2b. a "Business type" button (not in the demo)
//   3. icon buttons: Settings (every signed-in owner), Help, Sign out (or Help and "Get started" in the demo)

const FONT = "'DM Sans',sans-serif";
const iconButton = {
  flex: "1 1 0", minWidth: 0, height: 30, borderRadius: 4, border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.06)", cursor: "pointer", display: "flex", alignItems: "center",
  justifyContent: "center", color: "#C8B89E", fontSize: 13, fontFamily: FONT, padding: 0,
};
const textButton = (color) => ({
  border: `1px solid ${color}55`, background: "transparent", color, borderRadius: 4, padding: "4px 10px",
  fontSize: 11, fontFamily: FONT, cursor: "pointer", whiteSpace: "nowrap", lineHeight: 1.3,
});

const SidebarFooter = ({
  name, isQuickBooks, qbConnected, syncing, demo, hasTrackedRules,
  onSync, onOpenSettings, onOpenHelp, onSignOut, gearIcon, onOpenBusinessType,
}) => {
  const showQb = isQuickBooks && !demo;
  const label = name || "";
  return (
    <div style={{ padding: "12px 14px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Row 1: avatar and business name */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: isQuickBooks && qbConnected ? `${ACCENT2}33` : "rgba(245,239,227,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#F5EFE3", flexShrink: 0 }}>
          {(name || "U")[0].toUpperCase()}
        </div>
        <div title={label} style={{ flex: 1, minWidth: 0, fontSize: 12, fontFamily: FONT, color: "#F5EFE3", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
      </div>

      {/* Row 2: QuickBooks status and its actions */}
      {demo && <div style={{ fontSize: 11, fontFamily: FONT, color: SIDEBAR_DIM }}>Sample numbers</div>}
      {showQb && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {syncing
              ? <span className="spinner" style={{ width: 8, height: 8, borderWidth: 1, flexShrink: 0 }} />
              : <div style={{ width: 7, height: 7, borderRadius: "50%", background: qbConnected ? ACCENT2 : AMBER, flexShrink: 0 }} />}
            <span style={{ fontSize: 11, fontFamily: FONT, color: qbConnected ? ACCENT2 : AMBER, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {syncing ? "Syncing…" : qbConnected ? "QuickBooks connected" : "Not connected"}
            </span>
          </div>
          {qbConnected && !syncing && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <button type="button" onClick={onSync} style={textButton("#C8B89E")}>Sync now</button>
              {!hasTrackedRules && <button type="button" onClick={onOpenSettings} style={textButton(AMBER)}>Set up</button>}
            </div>
          )}
        </div>
      )}

      {/* Business type: how costs are named (hidden in the demo, which always uses Trades) */}
      {!demo && onOpenBusinessType && (
        <button type="button" onClick={onOpenBusinessType} style={{ ...textButton("#C8B89E"), alignSelf: "flex-start" }}>Business type</button>
      )}

      {/* Row 3: icon buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {!demo && onOpenSettings && (
          <button type="button" onClick={onOpenSettings} title="Settings" aria-label="Settings" style={iconButton}>{gearIcon}</button>
        )}
        <button type="button" onClick={onOpenHelp} title="Help" aria-label="Open tutorial" style={iconButton}>?</button>
        {demo ? (
          <a href={DEMO_SIGNUP_HREF} title={DEMO_SIGNUP_LABEL} style={{ ...iconButton, flex: "2 1 0", textDecoration: "none", color: "#F5EFE3", fontSize: 11 }}>{DEMO_SIGNUP_LABEL}</a>
        ) : (
          <button type="button" onClick={onSignOut} title="Sign out" aria-label="Sign out" style={iconButton}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        )}
      </div>
    </div>
  );
};

export { SidebarFooter };
